<template>
  <div>
    <configurar-associacao-fila-status-ticket-dialog
      v-if="associacaoSelecionada"
      :is-configurar-associacao-fila-status-ticket-dialog-open.sync="isConfigurarAssociacaoFilaFormularioDialogOpen"
      :loading="loading"
      :fila-status-data="associacaoSelecionada"
      :ja-possui-status-em-andamento-principal="jaPossuiStatusEmAndamentoPrincipal"
      :categorias-permitidas="categoriasPermitidas"
      :ordens-permitidas="ordensPermitidas"
      @refetch-data="$emit('refetch-data')"
    />

    <ativar-ou-inativar-associacao-dialog
      :is-ativar-ou-inativar-associacao-dialog-open.sync="isAtivarOuInativarAssociacaoFilaStatusTicketDialogOpen"
      :is-associacao-ativa-mensagem="isAssociacaoAtivaMensagem"
      :loading="loading"
      :close-dialog="closeDialogAtivarOuInativarAssociacao"
      :confirmar-ativacao-ou-inativacao-da-associacao="confirmarAtivacaoOuInativacaoDaAssociacao"
    />

    <v-data-table
      :headers="headers"
      :items="items"
      :acesso-disable="acessoDisable"
      :footer-props="{
        'items-per-page-text': 'Linhas por página',
        'page-text': '{0}-{1} de {2}',
        'items-per-page-all-text': 'Todos',
      }"
      :items-per-page="5"
      :item-class="cursorPointer"
      @click:row="selectAssociacao"
    >
      <template #[`item.nome`]="{ item }">
        <div class="d-flex align-center">
          <v-avatar
            :color="`${$vuetify.theme.dark ? 'primary' : '#DCDCDC'}`"
            size="32"
          >
            <span :class="`${$vuetify.theme.dark ? 'white--text' : 'primary--text'} font-weight-bold`">
              {{ item.nome.toUpperCase().charAt(0) }}
            </span>
          </v-avatar>
          <div class="d-flex flex-column ms-3">
            {{ item.nome }}
          </div>
        </div>
      </template>

      <template #[`item.descricao`]="{ item }">
        <div class="d-flex justify-center text-capitalize">
          {{ item.descricao }}
        </div>
      </template>

      <template #[`item.categoriaStatusNome`]="{ item }">
        <div>
          <v-tooltip
            v-if="item.principal"
            top
            color="primary"
          >
            <template #activator="{ on, attrs }">
              <v-chip
                color="primary"
                v-bind="attrs"
                outlined
                v-on="on"
              >
                {{ item.categoriaStatusNome }}
              </v-chip>
            </template>

            <span>Principal</span>
          </v-tooltip>

          <v-chip
            v-else
            color="accent"
            outlined
          >
            {{ item.categoriaStatusNome }}
          </v-chip>
        </div>
      </template>

      <template #[`item.ativo`]="{ item }">
        <v-chip
          class="v-chip-light-bg font-weight-semibold text-capitalize"
          small
          :class="`${item.ativo ? 'success--text' : 'secondary--text'}`"
          :color="`${item.ativo ? 'success' : 'secondary'}`"
        >
          {{ item.ativo ? 'Ativo' : 'Inativo' }}
        </v-chip>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-menu
          bottom
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              class="ml-n5"
              color="primary"
              v-on="on"
              @click="ativarOuInativarAssociacaoItem(item)"
            >
              <v-icon>
                {{ icons.mdiDotsVertical }}
              </v-icon>
            </v-btn>
          </template>

          <v-list v-if="acessoDisable">
            <v-list-item>
              <v-list-item-title
                role="button"
                class="d-flex align-end"
                @click="openDialogAtivarOuInativarAssociacao"
              >
                <v-icon
                  size="20"
                  class="mr-1"
                >
                  {{ isAssociacaoAtiva ? icons.mdiCloseThick : icons.mdiCheckBold }}
                </v-icon>
                <span class="text-capitalize"> {{ isAssociacaoAtivaMensagem }} </span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mdiCheckBold, mdiCloseThick, mdiDotsVertical } from '@mdi/js'
import { ref } from '@vue/composition-api'
import ConfigurarAssociacaoFilaStatusTicketDialog from './ConfigurarAssociacaoFilaStatusTicketDialog.vue'
import AtivarOuInativarAssociacaoDialog from '@/views/ativar-ou-inativar-associacao-dialog/AtivarOuInativarAssociacaoDialog.vue'
import FilaService from '@/services/modules/FilaService'
import FilaStatusTicketModel from '@/models/entities/fila/FilaStatusTicketModel'
import CategoriasStatusService from '@/services/modules/CategoriasStatusService'
import TicketCategoriaStatusEnum from '@/models/entities/ticket/TicketCategoriaStatusEnum'

export default {
  components: {
    ConfigurarAssociacaoFilaStatusTicketDialog,
    AtivarOuInativarAssociacaoDialog,
  },

  emits: ['refetch-data'],

  props: {
    items: {
      type: Array,
      required: true,
    },
    acessoDisable: {
      type: Boolean,
      default: true,
    },
  },

  setup(props, { emit }) {
    const isAtivarOuInativarAssociacaoFilaStatusTicketDialogOpen = ref(false)
    const isConfigurarAssociacaoFilaFormularioDialogOpen = ref(false)

    const loading = ref(false)
    const filaId = ref(0)
    const statusTicketId = ref(0)
    const isAssociacaoAtiva = ref(undefined)
    const isAssociacaoAtivaMensagem = ref('')
    const associacaoSelecionada = ref(null)
    const jaPossuiStatusEmAndamentoPrincipal = ref(false)
    const categoriasPermitidas = ref([])
    const ordensPermitidas = ref([])

    function ativarOuInativarAssociacaoItem(item) {
      filaId.value = item.filaId
      statusTicketId.value = item.id
      isAssociacaoAtiva.value = item.ativo
      isAssociacaoAtivaMensagem.value = item.ativo ? 'inativar' : 'ativar'
    }
    function openDialogAtivarOuInativarAssociacao() {
      isAtivarOuInativarAssociacaoFilaStatusTicketDialogOpen.value = true
    }
    function closeDialogAtivarOuInativarAssociacao() {
      isAtivarOuInativarAssociacaoFilaStatusTicketDialogOpen.value = false
    }
    async function confirmarAtivacaoOuInativacaoDaAssociacao() {
      try {
        loading.value = true
        await FilaService.ativarOuInativarFilaStatusTicket(filaId.value, statusTicketId.value, { ativar: !isAssociacaoAtiva.value })
        emit('refetch-data')
      } finally {
        loading.value = false
        closeDialogAtivarOuInativarAssociacao()
      }
    }

    async function getCategoriasPermitidas() {
      categoriasPermitidas.value = await CategoriasStatusService.getCategoriasStatus()
      const possuiStatusFinalizadoAlemDoSelecionado = props.items.find(s => s.id !== associacaoSelecionada.value.id && s.categoriaStatusNome === 'Finalizado') !== undefined
      if (possuiStatusFinalizadoAlemDoSelecionado) {
        categoriasPermitidas.value = categoriasPermitidas.value.filter(c => c.nome !== 'Finalizado')
      }
    }
    async function setOrdensPermitidas() {
      const ordensCadastradasDesconsiderandoASelecionada = ref(props.items.filter(it => it.id !== associacaoSelecionada.value.id).map(s => s.ordem))
      const jaCadastrouTodasAsOrdens = ordensCadastradasDesconsiderandoASelecionada.value.length === 50

      if (!jaCadastrouTodasAsOrdens) {
        ordensPermitidas.value = [...Array(50).keys()]
          .map(n => n + 1)
          .filter(n => !ordensCadastradasDesconsiderandoASelecionada.value.some(o => o === n))
      }
    }

    async function selectAssociacao(item) {
      if (!item.ativo) return
      try {
        loading.value = true
        associacaoSelecionada.value = new FilaStatusTicketModel(item)
        isConfigurarAssociacaoFilaFormularioDialogOpen.value = true
        jaPossuiStatusEmAndamentoPrincipal.value = props.items.some(it => it.id !== item.id && it.categoriaStatusId === TicketCategoriaStatusEnum.Andamento && it.principal)
        await getCategoriasPermitidas()
        await setOrdensPermitidas()
      } finally {
        loading.value = false
      }
    }

    const headers = [
      { text: 'NOME', value: 'nome' },
      { text: 'CATEGORIA STATUS', value: 'categoriaStatusNome' },
      { text: 'STATUS', value: 'ativo' },
      {
        text: 'OPÇÔES',
        value: 'actions',
        align: 'center',
      },
    ]

    return {
      isAtivarOuInativarAssociacaoFilaStatusTicketDialogOpen,
      isConfigurarAssociacaoFilaFormularioDialogOpen,
      loading,
      filaId,
      statusTicketId,
      isAssociacaoAtiva,
      isAssociacaoAtivaMensagem,
      associacaoSelecionada,
      jaPossuiStatusEmAndamentoPrincipal,
      categoriasPermitidas,
      ordensPermitidas,
      ativarOuInativarAssociacaoItem,
      openDialogAtivarOuInativarAssociacao,
      closeDialogAtivarOuInativarAssociacao,
      confirmarAtivacaoOuInativacaoDaAssociacao,
      selectAssociacao,
      headers,
      icons: {
        mdiCheckBold,
        mdiCloseThick,
        mdiDotsVertical,
      },
      cursorPointer: item => (item.ativo || item.ativo === null ? 'cursor-pointer' : ''),
    }
  },
}
</script>
